import { useMemo } from "react";
import { useSetups } from "../../../../../../api/useSetups";
import { useIndexedFailureValues } from "../../../../../../api/useIndexedFailureValues";
import { useIndexedFailureMetadata } from "../../../../../../api/useFailureMetadata";
import { useQuantities, type Quantity } from "../../../../../../api/useQuantities";
import type { Project } from "../../../../../../api/useProjects";
import { useActiveAnalysesPeriod } from "../../useAnalysesPeriod";
import { uniqBy } from "diagnostics-lib/array";
import { indexBy } from "diagnostics-lib/iterable";
import useSeries, { type SparklineEvent } from "./useSeries";
import useOptions from "./useOptions";
import type { ActiveAnalysis } from "../../../../../../api/useActiveAnalysesStream";
import { computeFaultImpactOverPeriod } from "diagnostics-domain/aggregationPeriod/faultValueOverPeriod";
import { findPowerQuantityId } from "diagnostics-domain/aggregationPeriod";
import type { Event } from "../../../../../../api/useEvents";
import { getManualFaultImpactsByAnalysis } from "diagnostics-domain/manualFaultEvent";
import { useIndexedManualFailureValues } from "../../../../../../api/useManualFailureValues";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import { getEventEndsAt, getEventId } from "../../../../../../domain/event";
import { isFailureValueInFaultEventOccurrencePeriods } from "diagnostics-domain/faultEvent";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";

const yAxisId = "fault-event-impacts";

const useSparkline = ({
  project,
  performanceIndicator,
  aggregationPeriod,
  activeAnalyses,
  events,
}: {
  project: Project;
  performanceIndicator: Quantity | null;
  aggregationPeriod: { from: Date; to: Date };
  activeAnalyses: ActiveAnalysis[];
  events: Event[];
}) => {
  const stableEvents = useDistinctShape(events);
  const aggregationPeriodFrom = aggregationPeriod.from.getTime();
  const aggregationPeriodTo = aggregationPeriod.to.getTime();
  const quantities = useQuantities(project.id);
  const performanceIndicators = useMemo(
    () => quantities.filter(withKeyNotNull("qoiOrder")),
    [quantities],
  );
  const indexedQuantities = useMemo(() => indexBy(quantities, "id"), [quantities]);
  const setups = useSetups(project.id);
  const indexedFailureValues = useIndexedFailureValues(project.id);
  const indexedManualFailureValues = useIndexedManualFailureValues(project.id);

  const indexedFailureMetadata = useIndexedFailureMetadata(project.id);
  const { activeAnalysesPeriod } = useActiveAnalysesPeriod(aggregationPeriod, activeAnalyses);
  const activeAnalysesPeriodDates = useMemo(
    () => activeAnalysesPeriod.map(a => a.date),
    [activeAnalysesPeriod],
  );
  const failureValues = useMemo(
    () =>
      uniqBy(
        Object.values(indexedFailureValues)
          .flatMap(failureValues =>
            Object.values(failureValues).map(fv => ({
              date: fv.date,
              failureName: fv.failureName,
              real: fv.impacts,
            })),
          )
          .filter(fv => fv.date >= aggregationPeriodFrom && fv.date <= aggregationPeriodTo),
        f => `${f.failureName}-${f.date}`,
      ),
    [aggregationPeriodFrom, aggregationPeriodTo, indexedFailureValues],
  );

  const eventStatistics = useMemo(() => {
    const powerQuantityId = findPowerQuantityId(quantities);
    const powerQuantity = powerQuantityId ? (indexedQuantities.get(powerQuantityId) ?? null) : null;

    if (!performanceIndicator || !powerQuantity) return [];

    return stableEvents.flatMap(event => {
      const { metadata, type } = event;

      switch (type) {
        case "DIAGNOSED": {
          const meanImpacts = metadata.faults
            .map(fault => {
              const faultValuesOverPeriod = failureValues.filter(
                fv =>
                  fv.failureName === fault.name &&
                  isFailureValueInFaultEventOccurrencePeriods(event, {
                    projectId: event.projectId,
                    ...fv,
                  }) &&
                  fv.date >= aggregationPeriodFrom &&
                  fv.date <= aggregationPeriodTo,
              );

              const faultStatusOverPeriod = fault.statusByAnalysis.map(status => ({
                ...status,
                failureName: fault.name,
              }));

              return computeFaultImpactOverPeriod(
                powerQuantity.id,
                activeAnalysesPeriodDates,
                faultValuesOverPeriod,
                faultStatusOverPeriod,
              );
            })
            .filter((meanImpact): meanImpact is number => meanImpact !== null);

          if (meanImpacts.length === 0) return [];

          return [
            {
              id: event.id,
              mean: meanImpacts.reduce((total, meanImpact) => total + meanImpact, 0),
            },
          ];
        }
        case "MANUAL": {
          const manualFaultValues = getManualFaultImpactsByAnalysis({
            manualFaultEvent: {
              failureName: metadata.manualFault.name,
              windows: event.windows,
              impacts: event.impacts,
              endsAt: event.metadata.endsAt,
            },
            activeAnalyseDatesPeriod: activeAnalysesPeriodDates,
            performanceIndicators,
          });

          const meanImpacts = computeFaultImpactOverPeriod(
            powerQuantity.id,
            activeAnalysesPeriodDates,
            manualFaultValues,
            [],
          );

          if (meanImpacts === null) return [];
          return [{ id: event.id, mean: meanImpacts }];
        }
        default:
          return [];
      }
    });
  }, [
    activeAnalysesPeriodDates,
    quantities,
    indexedQuantities,
    performanceIndicator,
    stableEvents,
    failureValues,
    aggregationPeriodFrom,
    aggregationPeriodTo,
    performanceIndicators,
  ]);

  const sparklineStatisticByEventId = useMemo(
    () => indexBy(eventStatistics, "id"),
    [eventStatistics],
  );

  const sparklineSeries = useSeries({
    yAxisId,
    setups,
    analyses: activeAnalysesPeriod,
    indexedFailureValues,
    indexedManualFailureValues,
    indexedFailureMetadata,
    qoi: performanceIndicator ?? null,
    events: stableEvents.map<SparklineEvent>(event => {
      const id = getEventId(event);

      switch (event.type) {
        case "DIAGNOSED":
          return {
            id,
            failures: event.metadata.faults.map(fault => ({
              name: fault.name,
              occurrencePeriods: fault.occurrencePeriods.map(period => ({
                startsAt: period.startsAt.getTime(),
                endsAt: period.endsAt.getTime(),
              })),
            })),
            startsAt: event.startsAt.getTime(),
            endsAt: getEventEndsAt(event).getTime(),
            type: event.type,
          };

        case "MANUAL":
          return {
            id,
            failureName: event.metadata.manualFault.name,
            failureId: event.metadata.manualFault.id,
            startsAt: event.startsAt.getTime(),
            endsAt: getEventEndsAt(event).getTime(),
            type: event.type,
          };

        default:
          throw new Error(event satisfies never);
      }
    }),
  });

  const sparklineOptions = useOptions({
    projectId: project.id,
    minPeriod: aggregationPeriodFrom,
    maxPeriod: aggregationPeriodTo,
    sparklineSeries,
  });
  const sparklineOptionsByEventId = useMemo(
    () => indexBy(sparklineOptions, "id"),
    [sparklineOptions],
  );
  return {
    sparklineStatisticByEventId,
    sparklineOptionsByEventId,
  };
};

export default useSparkline;
